import React, { useEffect, useRef, useState } from 'react';
import { BrainIcon, AnalyticsIcon, ZapIcon, TargetIcon, CheckCircleIcon } from '../common/icons';
import './FeatureShowcase.css';

interface Feature {
  id: string;
  icon: React.ComponentType<{ width?: number; height?: number; color?: string; className?: string }>;
  title: string;
  description: string;
  benefits: string[];
  metrics?: {
    value: string;
    label: string;
  };
}

interface FeatureShowcaseProps {
  onLearnMore?: (featureId: string) => void;
}

const FeatureShowcase: React.FC<FeatureShowcaseProps> = ({ onLearnMore }) => {
  const [visibleFeatures, setVisibleFeatures] = useState<Set<string>>(new Set());
  const observerRef = useRef<IntersectionObserver | null>(null);

  const features: Feature[] = [
    {
      id: 'ai-optimization',
      icon: BrainIcon,
      title: 'AI-Powered Optimization',
      description: 'Advanced machine learning algorithms analyze your supply chain data to identify optimal inventory allocation strategies and minimize operational costs.',
      benefits: [
        'Reduce inventory costs by up to 40%',
        'Minimize stockouts and overstock situations',
        'Automated decision-making processes',
        'Continuous learning and improvement'
      ],
      metrics: {
        value: '40%',
        label: 'Cost Reduction'
      }
    },
    {
      id: 'real-time-analytics',
      icon: AnalyticsIcon,
      title: 'Real-Time Analytics',
      description: 'Monitor your warehouse operations with live dashboards, instant alerts, and comprehensive reporting to make data-driven decisions.',
      benefits: [
        'Live performance monitoring',
        'Instant alert notifications',
        'Comprehensive reporting suite',
        'Historical trend analysis'
      ],
      metrics: {
        value: '99.9%',
        label: 'Uptime'
      }
    },
    {
      id: 'scalable-architecture',
      icon: ZapIcon,
      title: 'Scalable Architecture',
      description: 'Built on cloud-native infrastructure that grows with your business, handling everything from small operations to enterprise-scale deployments.',
      benefits: [
        'Auto-scaling capabilities',
        'Multi-region deployment',
        'High availability guarantee',
        'Enterprise-grade security'
      ],
      metrics: {
        value: '1000+',
        label: 'Warehouses'
      }
    },
    {
      id: 'intelligent-forecasting',
      icon: TargetIcon,
      title: 'Intelligent Forecasting',
      description: 'Predict future demand patterns using advanced statistical models and machine learning to optimize inventory levels and reduce waste.',
      benefits: [
        'Accurate demand prediction',
        'Seasonal trend analysis',
        'Risk assessment and mitigation',
        'Automated reorder points'
      ],
      metrics: {
        value: '95%',
        label: 'Accuracy'
      }
    }
  ];

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleFeatures(prev => new Set([...Array.from(prev), entry.target.id]));
          }
        });
      },
      { threshold: 0.2 }
    );

    const featureElements = document.querySelectorAll('.feature-card');
    featureElements.forEach(el => observerRef.current?.observe(el));

    return () => {
      observerRef.current?.disconnect();
    };
  }, []);

  return (
    <section className="feature-showcase" aria-labelledby="features-heading">
      <div className="showcase-container">
        <div className="showcase-header">
          <h2 id="features-heading" className="showcase-title">
            Powerful Features for Modern Supply Chains
          </h2>
          <p className="showcase-subtitle">
            Discover how Optinode AI transforms warehouse operations with cutting-edge technology 
            and intelligent automation designed for today's complex supply chain challenges.
          </p>
        </div>

        <div className="features-grid">
          {features.map((feature, index) => (
            <article 
              key={feature.id}
              id={feature.id}
              className={`feature-card ${visibleFeatures.has(feature.id) ? 'visible' : ''}`}
              style={{ animationDelay: `${index * 0.2}s` }}
            >
              <div className="feature-header">
                <div className="feature-icon">
                  <feature.icon width={32} height={32} color="#007AFF" />
                </div>
                <div className="feature-metric">
                  <span className="metric-value">{feature.metrics?.value}</span>
                  <span className="metric-label">{feature.metrics?.label}</span>
                </div>
              </div>
              
              <div className="feature-content">
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
                
                <ul className="feature-benefits">
                  {feature.benefits.map((benefit, idx) => (
                    <li key={idx} className="benefit-item">
                      <CheckCircleIcon width={16} height={16} color="#10B981" className="benefit-check" />
                      {benefit}
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="feature-actions">
                <button 
                  className="btn btn-outline"
                  onClick={() => onLearnMore?.(feature.id)}
                  aria-label={`Learn more about ${feature.title}`}
                >
                  Learn More
                </button>
              </div>
            </article>
          ))}
        </div>

        <div className="showcase-cta">
          <div className="cta-content">
            <h3 className="cta-title">Ready to Transform Your Operations?</h3>
            <p className="cta-description">
              Join thousands of companies already using Optinode AI to optimize their supply chains.
            </p>
            <button className="btn btn-primary btn-large">
              Start Free Trial
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeatureShowcase;